// #25 automatic payments, phase 4: the pure logic behind the cards the signatories claim when automatic payments need them
// (Payment failed, Limit reached, Change target, Re-sign allowance) and the My Account lines saying where automatic payments
// stand. Every card's figures come from the job metadata the lambda that raised it wrote (ns-lambda-ts
// _core/public-ledger/auto-cards.ts and its callers); every outcome is the server's, completed by the lambda that checked
// the action (public-ledger/autopay/card-action, public-ledger/autopay/resign).
//
// #25 phase 6: Raise (Limit reached), Change target (Change target) and Raise (Raise operating target) start a change of
// amounts as the card's child (public-ledger/autopay/card-action); the card closes when the child ends. Change card on a
// Payment failed card is entered on the card itself (public-ledger/autopay/change-intent, confirm-change-card).
import { formatCredits } from './public-ledger-credits.js';
import { formatPence, formatUnitPence } from './credit-pricing.js';
import type { AutoPaymentRow } from './auto-payments.js';

// The refusals the card actions answer, beyond the shared ones in auto-payments.ts AUTOPAY_CODES
export const AUTOPAY_CARD_CODES = {
  nothingToConfirm: 'PUBLIC_LEDGER_AUTOPAY_NOTHING_TO_CONFIRM',
  alreadyPaid: 'PUBLIC_LEDGER_AUTOPAY_ALREADY_PAID',
  monthEnded: 'PUBLIC_LEDGER_AUTOPAY_MONTH_ENDED',
  allowanceInPlace: 'PUBLIC_LEDGER_AUTOPAY_ALLOWANCE_IN_PLACE',
  chargesStopped: 'PUBLIC_LEDGER_AUTOPAY_CHARGES_STOPPED',
} as const;

type Metadata = { key: string; value: string | null }[] | null | undefined;

const metadataValue = (metadata: Metadata, key: string): string | null => {
  const value = metadata?.find((m) => m.key === key)?.value;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};

// A whole, non-negative number as a lambda writes it to metadata, or null
function metadataWhole(metadata: Metadata, key: string): number | null {
  const text = metadataValue(metadata, key) ?? '';
  return /^\d+$/.test(text) ? Number(text) : null;
}

// A non-negative decimal (a price per credit in pence), or null
function metadataDecimal(metadata: Metadata, key: string): number | null {
  const text = metadataValue(metadata, key) ?? '';
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : null;
}

// --- Months, in UK time (#25 DECISION 3.4) ---

// The last Sunday of a month (0-11) of a year, at 01:00 UTC, as ms
function lastSundayAt0100Utc(year: number, month: number): number {
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  return Date.UTC(year, month, lastDay.getUTCDate() - lastDay.getUTCDay(), 1);
}

/**
 * 'YYYY-MM' of a moment in UK time, as ns-lambda-ts _core/public-ledger/month.ts monthKey: British Summer Time (UTC+1)
 * from 01:00 UTC on the last Sunday of March to 01:00 UTC on the last Sunday of October, GMT otherwise. Worked out by hand,
 * like the server, so both agree whatever time-zone data the browser has.
 */
export function londonMonthKey(now: Date | number = Date.now()): string {
  const ms = typeof now === 'number' ? now : now.getTime();
  if (!Number.isFinite(ms)) throw new Error('londonMonthKey: not a date');
  const year = new Date(ms).getUTCFullYear();
  const bst = ms >= lastSundayAt0100Utc(year, 2) && ms < lastSundayAt0100Utc(year, 9);
  const local = new Date(ms + (bst ? 3600000 : 0));
  return `${local.getUTCFullYear()}-${String(local.getUTCMonth() + 1).padStart(2, '0')}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function parseMonth(month: string | null | undefined): { year: number; index: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!match) return null;
  const index = Number(match[2]) - 1;
  return index >= 0 && index < 12 ? { year: Number(match[1]), index } : null;
}

// '2026-10' → 'October 2026'
export function monthLabel(month: string | null | undefined): string | null {
  const parsed = parseMonth(month);
  return parsed ? `${MONTHS[parsed.index]} ${parsed.year}` : null;
}

// '2026-10' → '1 November 2026', the day a month's limit lifts
export function monthAfterLabel(month: string | null | undefined): string | null {
  const parsed = parseMonth(month);
  if (!parsed) return null;
  const next =
    parsed.index === 11 ? { year: parsed.year + 1, index: 0 } : { year: parsed.year, index: parsed.index + 1 };
  return `1 ${MONTHS[next.index]} ${next.year}`;
}

// --- Payment failed ---

export type ChargeFailure = 'DECLINED' | 'EXPIRED' | 'AUTH_REQUIRED';

export interface ChargeFailedFigures {
  purchaseId: string | null;
  failure: ChargeFailure | null;
  declineCode: string | null;
  amountPence: number | null;
  credits: number | null;
  month: string | null;
  cardOwnerId: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  // Raised again after a change of card wasn't made (#25 phase 6)
  reopened: boolean;
}

// The Payment failed card's text when the bank wants to check the payment with the cardholder, who holds the card
export const BANK_APPROVAL_TEXT =
  'Your bank needs to check this payment with you before it goes through. Press Confirm payment, then approve it in ' +
  'the window your bank opens or in your banking app.';

// The first line of a Payment failed card raised again because a change of card wasn't made
export const CHARGE_FAILED_REOPENED_TEXT =
  "The last change of card wasn't made, so automatic payments are still paused. Retry the card or change it again.";

const FAILURES: readonly ChargeFailure[] = ['DECLINED', 'EXPIRED', 'AUTH_REQUIRED'];

// The Payment failed card's figures, from the metadata autopay/charge (or the payment-failed webhook) wrote
export function chargeFailedFigures(metadata: Metadata): ChargeFailedFigures {
  const failure = metadataValue(metadata, 'FAILURE');
  return {
    purchaseId: metadataValue(metadata, 'PURCHASE_ID'),
    failure: FAILURES.includes(failure as ChargeFailure) ? (failure as ChargeFailure) : null,
    declineCode: metadataValue(metadata, 'DECLINE_CODE'),
    amountPence: metadataWhole(metadata, 'AMOUNT_PENCE'),
    credits: metadataWhole(metadata, 'CREDITS'),
    month: metadataValue(metadata, 'MONTH'),
    cardOwnerId: metadataValue(metadata, 'CARD_OWNER_ID'),
    cardBrand: metadataValue(metadata, 'CARD_BRAND'),
    cardLast4: metadataValue(metadata, 'CARD_LAST4'),
    reopened: metadataValue(metadata, 'REOPENED') === 'true',
  };
}

// What a card issuer's decline code means, for the codes people meet most; anything else is a plain decline
const DECLINE_REASONS: Record<string, string> = {
  insufficient_funds: 'the card had insufficient funds',
  expired_card: 'the card has expired',
  lost_card: 'the card was reported lost',
  stolen_card: 'the card was reported stolen',
  incorrect_cvc: 'the card’s security code was wrong',
  card_velocity_exceeded: 'the card’s spending limit was reached',
  withdrawal_count_limit_exceeded: 'the card’s spending limit was reached',
  do_not_honor: 'the bank declined it without giving a reason',
  generic_decline: 'the bank declined it without giving a reason',
  card_not_supported: 'the card doesn’t support this kind of payment',
  currency_not_supported: 'the card doesn’t support payments in pounds',
  fraudulent: 'the bank suspected fraud',
};

// Why the payment failed, in a sentence
export function chargeFailureText(failure: ChargeFailure | null, declineCode: string | null): string {
  if (failure === 'AUTH_REQUIRED') {
    return 'The bank wants the card’s owner to confirm this payment before it goes through.';
  }
  if (failure === 'EXPIRED') return 'The card has expired, so the payment was declined.';
  // nodeStream couldn't confirm its own earlier request for this payment: the card itself didn't decline
  if (declineCode === 'idempotency_error') return 'The payment couldn’t be confirmed. Retry charges the card again.';
  // The payment processor refused the request itself, for example because the saved card was removed: not the bank
  if (declineCode === 'invalid_request') {
    return 'The payment couldn’t be started, for example because the saved card is no longer available. Retry charges the card again.';
  }
  const reason = declineCode ? DECLINE_REASONS[declineCode] : undefined;
  return reason ? `The payment was declined: ${reason}.` : 'The bank declined the payment.';
}

export type ChargeFailedAction = 'CLAIM' | 'CONFIRM' | 'RETRY' | 'CHANGE_CARD';

/**
 * Why a Retry would charge nothing now, as card-action says when it refuses one (409 PUBLIC_LEDGER_AUTOPAY_CHARGES_STOPPED),
 * or null: the target buys no more than the threshold, or this UK month's card limit is reached.
 */
export function chargesStoppedReason(
  row: Pick<AutoPaymentRow, 'targetHealth' | 'monthStops'> | null | undefined,
  now: Date | number = Date.now(),
): string | null {
  if (row?.targetHealth === 'STOPPED') {
    return 'Automatic charges are stopped because the target no longer buys more credits than the threshold. The payment can be tried again once the price comes back.';
  }
  if (row?.monthStops?.card && row.monthStops.card === londonMonthKey(now)) {
    return 'Automatic charges are stopped for the rest of this month, as the monthly card limit was reached. The payment can be tried again next month.';
  }
  return null;
}

/**
 * The buttons the Payment failed card offers, in order (the first is the filled one):
 * - unclaimed: Claim;
 * - Confirm payment only when the bank asked for the owner's confirmation and the viewer is the card's owner (its live
 *   `cardOwnerId`, else the one the card was raised with); the server checks the same (403 otherwise);
 * - Retry, except while charges are stopped (chargesStoppedReason): the server refuses it then;
 * - Change card (#25 phase 6): the new card is entered on this card; change-intent refuses it while another change of
 *   card is open or waiting for signatures.
 * Nothing once the card is done.
 */
export function chargeFailedActions(input: {
  completed: boolean;
  claimed: boolean;
  failure: ChargeFailure | null;
  personId: string | null | undefined;
  cardOwnerId: string | null | undefined;
  chargesStopped?: boolean;
}): ChargeFailedAction[] {
  if (input.completed) return [];
  if (!input.claimed) return ['CLAIM'];
  const actions: ChargeFailedAction[] = [];
  const owner = !!input.personId && input.personId === input.cardOwnerId;
  if (input.failure === 'AUTH_REQUIRED' && owner) actions.push('CONFIRM');
  if (!input.chargesStopped) actions.push('RETRY');
  actions.push('CHANGE_CARD');
  return actions;
}

export type ConfirmStep = 'PAID' | 'BANK_STEP' | 'PROCESSING' | 'NOT_CONFIRMED';

/**
 * What the card does after card-action confirm or confirmed answers: paid (the server completes the card), the bank's step
 * in the browser (requires_action with a client secret), still processing, or not confirmed (the bank refused, or there is
 * no step to run).
 */
export function confirmStep(answer: { status?: unknown; clientSecret?: unknown } | null | undefined): ConfirmStep {
  const status = answer?.status;
  if (status === 'succeeded') return 'PAID';
  if (status === 'processing') return 'PROCESSING';
  if (status === 'requires_action' && typeof answer?.clientSecret === 'string' && answer.clientSecret) {
    return 'BANK_STEP';
  }
  return 'NOT_CONFIRMED';
}

// --- Limit reached ---

export type LimitKind = 'CARD' | 'OPERATING';

export interface LimitReachedFigures {
  limit: LimitKind | null;
  month: string | null;
  limitPence: number | null;
  usedPence: number | null;
}

// The Limit reached card's figures, from the metadata autopay/charge (CARD) or autopay/top-up (OPERATING) wrote
export function limitReachedFigures(metadata: Metadata): LimitReachedFigures {
  const limit = metadataValue(metadata, 'LIMIT');
  return {
    limit: limit === 'CARD' || limit === 'OPERATING' ? limit : null,
    month: metadataValue(metadata, 'MONTH'),
    limitPence: metadataWhole(metadata, 'LIMIT_PENCE'),
    usedPence: metadataWhole(metadata, 'USED_PENCE'),
  };
}

// What has stopped, and until when, in a sentence
export function limitReachedText(figures: LimitReachedFigures): string {
  const month = monthLabel(figures.month) ?? 'this month';
  const lifts = monthAfterLabel(figures.month);
  const until = lifts ? ` They start again on ${lifts}, UK time.` : ' They start again next month.';
  if (figures.limit === 'OPERATING') {
    return `Your company’s monthly operating top-up limit is reached for ${month}, so automatic top-ups of the operating account have stopped.${until}`;
  }
  return `Your company’s monthly card limit is reached for ${month}, so automatic charges to the card have stopped.${until}`;
}

// Whether Close for the month is still offered: only during the card's own month (the server refuses it after, 409)
export function canCloseForMonth(figures: LimitReachedFigures, now: Date | number = Date.now()): boolean {
  return !!figures.limit && !!figures.month && figures.month === londonMonthKey(now);
}

export type LimitReachedAction = 'CLAIM' | 'RAISE' | 'CLOSE_FOR_MONTH';

/**
 * The buttons the Limit reached card offers, in order (the first is the filled one): Claim while unclaimed; then, during
 * the card's own month, Raise (#25 phase 6: changing the amounts lifts this month's stop for each limit it raises) and
 * Close for the month. Once the month is over the limit has lifted and the sweep closes the card, so nothing is offered;
 * nothing either once the card is done or waiting for the server.
 */
export function limitReachedActions(input: {
  completed: boolean;
  claimed: boolean;
  canClose: boolean;
}): LimitReachedAction[] {
  if (input.completed) return [];
  if (!input.claimed) return ['CLAIM'];
  return input.canClose ? ['RAISE', 'CLOSE_FOR_MONTH'] : [];
}

// --- Change target ---

export interface ChangeTargetFigures {
  reason: 'BELOW_PERCENT' | 'STOPPED' | null;
  targetPence: number | null;
  creditsNow: number | null;
  creditsAtSigning: number | null;
  thresholdCredits: number | null;
  alertPercent: number | null;
  unitPricePence: number | null;
}

// The Change target card's figures, from the metadata liquidity-check wrote (and keeps up to date while it is open)
export function changeTargetFigures(metadata: Metadata): ChangeTargetFigures {
  const reason = metadataValue(metadata, 'REASON');
  const unit = metadataDecimal(metadata, 'UNIT_PRICE_PENCE');
  return {
    reason: reason === 'BELOW_PERCENT' || reason === 'STOPPED' ? reason : null,
    targetPence: metadataWhole(metadata, 'TARGET_PENCE'),
    creditsNow: metadataWhole(metadata, 'TARGET_CREDITS_NOW'),
    creditsAtSigning: metadataWhole(metadata, 'TARGET_CREDITS_AT_SIGNING'),
    thresholdCredits: metadataWhole(metadata, 'THRESHOLD_CREDITS'),
    alertPercent: metadataWhole(metadata, 'ALERT_PERCENT'),
    unitPricePence: unit !== null && unit > 0 ? unit : null,
  };
}

// The problem, in a sentence: what the £ target buys now against what it must buy
export function changeTargetText(figures: ChangeTargetFigures): string {
  const target = figures.targetPence !== null ? `The ${formatPence(figures.targetPence)} target` : 'The target';
  const now = figures.creditsNow !== null ? ` buys ${formatCredits(figures.creditsNow)} credits at today’s price` : '';
  if (figures.reason === 'STOPPED') {
    const threshold =
      figures.thresholdCredits !== null
        ? `the threshold of ${formatCredits(figures.thresholdCredits)} credits`
        : 'the threshold';
    return `${target}${now}, no more than ${threshold}, so automatic charges to the card have stopped.`;
  }
  const signed =
    figures.creditsAtSigning !== null
      ? ` the ${formatCredits(figures.creditsAtSigning)} credits it bought when the settings were signed`
      : ' the credits it bought when the settings were signed';
  const percent = figures.alertPercent !== null ? `${figures.alertPercent}% of` : 'the share you chose of';
  return `${target}${now}, under ${percent}${signed}. Automatic charges go on.`;
}

// The figures behind it, for a detail list
export function changeTargetRows(figures: ChangeTargetFigures): { label: string; value: string | undefined }[] {
  return [
    {
      label: 'Company account target',
      value: figures.targetPence !== null ? formatPence(figures.targetPence) : undefined,
    },
    {
      label: 'Buys at today’s price',
      value: figures.creditsNow !== null ? `${formatCredits(figures.creditsNow)} credits` : undefined,
    },
    {
      label: 'Bought when the settings were signed',
      value: figures.creditsAtSigning !== null ? `${formatCredits(figures.creditsAtSigning)} credits` : undefined,
    },
    {
      label: 'Company account threshold',
      value: figures.thresholdCredits !== null ? `${formatCredits(figures.thresholdCredits)} credits` : undefined,
    },
    {
      label: 'Price alert',
      value: figures.alertPercent !== null ? `Below ${figures.alertPercent}% of the credits at signing` : 'Off',
    },
    {
      label: 'Price per credit today',
      value: figures.unitPricePence !== null ? formatUnitPence(figures.unitPricePence) : undefined,
    },
  ];
}

// --- Raise operating target (#25 phase 6) ---

export interface RaiseTargetFigures {
  neededCredits: number | null;
  operatingTargetCredits: number | null;
  suggestedTargetCredits: number | null;
}

// The Raise operating target card's figures, from the metadata liquidity-check wrote (and keeps up to date while it is open)
export function raiseTargetFigures(metadata: Metadata): RaiseTargetFigures {
  return {
    neededCredits: metadataWhole(metadata, 'NEEDED_CREDITS'),
    operatingTargetCredits: metadataWhole(metadata, 'OPERATING_TARGET_CREDITS'),
    suggestedTargetCredits: metadataWhole(metadata, 'SUGGESTED_TARGET_CREDITS'),
  };
}

// The figures behind it, for a detail list
export function raiseTargetRows(figures: RaiseTargetFigures): { label: string; value: string | undefined }[] {
  const credits = (value: number | null) => (value !== null ? `${formatCredits(value)} credits` : undefined);
  return [
    { label: 'Credits needed', value: credits(figures.neededCredits) },
    { label: 'Operating target now', value: credits(figures.operatingTargetCredits) },
    { label: 'Suggested operating target', value: credits(figures.suggestedTargetCredits) },
  ];
}

// --- Re-sign allowance ---

export type ResignStage = 'NOT_ACTIVE' | 'IN_PLACE' | 'CHANGE_OPEN' | 'READY';

/**
 * Where the Re-sign allowance card is, from the company's row:
 * - NOT_ACTIVE: automatic payments aren't on, so there is no allowance to re-sign;
 * - IN_PLACE: the allowance is back (allowanceHealth OK): nothing to re-sign;
 * - CHANGE_OPEN: settings are already waiting for signatures (a re-sign or another change);
 * - READY: the holder can propose the re-sign.
 * The resign lambda decides; this only chooses what the card shows and offers.
 */
export function resignStage(row: AutoPaymentRow | null | undefined): ResignStage {
  if (row?.setupState !== 'ACTIVE') return 'NOT_ACTIVE';
  if (row.allowanceHealth !== 'MISSING') return 'IN_PLACE';
  if (row.pendingChange?.transactionId) return 'CHANGE_OPEN';
  return 'READY';
}

// --- My Account ---

// The line while a job waits for the operating target to be raised (approved, round 4 follow-ups); My Account follows it
// with an Open link to the open Raise the automatic payments operating target card
export const OPERATING_TARGET_WAITING_TEXT =
  'A job is waiting for the operating target to be raised before it can be completed.';

// The Raise the automatic payments operating target card's job name, which that Open link looks for
export const RAISE_TARGET_JOB = '@ns/public-ledger-auto-payments-raise-target';

/**
 * What needs the signatories' attention in a company's automatic payments, a line each, most pressing first; empty when
 * nothing does or automatic payments aren't on. A month stop counts only in its own UK month: an earlier month's has
 * lapsed.
 */
export function autopayAttention(row: AutoPaymentRow | null | undefined, now: Date | number = Date.now()): string[] {
  if (row?.setupState !== 'ACTIVE') return [];
  const lines: string[] = [];
  const month = londonMonthKey(now);
  const lifts = monthAfterLabel(month);
  if (row.cardHealth === 'FAILED') {
    lines.push('The last automatic payment failed, so automatic charges have stopped until it is retried.');
  } else if (row.cardHealth === 'AUTH_REQUIRED') {
    lines.push('The bank wants the card’s owner to confirm the last automatic payment.');
  }
  if (row.targetHealth === 'STOPPED') {
    lines.push(
      'The target buys no more credits than the threshold at today’s price, so automatic charges have stopped.',
    );
  } else if (row.targetHealth === 'BELOW_PERCENT') {
    lines.push('The target buys fewer credits than the price alert allows. Automatic charges go on.');
  }
  if (row.operatingTargetWaiting === true) {
    // Approved (round 4 follow-ups); My Account adds an Open link to the Raise card when one is open
    lines.push(OPERATING_TARGET_WAITING_TEXT);
  }
  if (row.allowanceHealth === 'MISSING') {
    lines.push(
      row.pendingChange?.kind === 'RESIGN' && row.pendingChange.transactionId
        ? 'Automatic top-ups have paused: the allowance they draw on is waiting for the signatories to sign it again.'
        : 'Automatic top-ups have paused: the allowance they draw on is missing on the public ledger and needs signing again.',
    );
  }
  if (row.monthStops?.card === month) {
    lines.push(`The monthly card limit is reached: automatic charges start again on ${lifts}.`);
  }
  if (row.monthStops?.operating === month) {
    lines.push(`The monthly operating top-up limit is reached: automatic top-ups start again on ${lifts}.`);
  }
  return lines;
}
